// ── VIDEO URL PARSING ────────────────────────────────────────────

/**
 * Parse a YouTube or Vimeo URL and extract provider, ID, and thumbnail URL.
 * Returns null for unsupported or invalid URLs.
 * @param {string} url
 * @returns {{ provider: 'youtube' | 'vimeo', id: string, thumbnailUrl: string } | null}
 */
function parseVideoUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }
  let parsed;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }

  const host = parsed.hostname.replace(/^www\./, '');

  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    let id = parsed.searchParams.get('v');
    if (!id) {
      const m = parsed.pathname.match(/^\/(embed|shorts)\/([a-zA-Z0-9_-]{11})/);
      if (m) {
        id = m[2];
      }
    }
    if (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) {
      return {
        provider: 'youtube',
        id,
        thumbnailUrl: `https://img.youtube.com/vi/${id}/maxresdefault.jpg`,
      };
    }
  }

  if (host === 'youtu.be') {
    const id = parsed.pathname.slice(1).split(/[?/]/)[0];
    if (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) {
      return {
        provider: 'youtube',
        id,
        thumbnailUrl: `https://img.youtube.com/vi/${id}/maxresdefault.jpg`,
      };
    }
  }

  // Vimeo: vimeo.com/ID, player.vimeo.com/video/ID
  if (host === 'vimeo.com') {
    const id = parsed.pathname.split('/').filter(Boolean)[0];
    if (id && /^\d+$/.test(id)) {
      return { provider: 'vimeo', id, thumbnailUrl: '' };
    }
  }

  if (host === 'player.vimeo.com') {
    const m = parsed.pathname.match(/^\/video\/(\d+)/);
    if (m) {
      return { provider: 'vimeo', id: m[1], thumbnailUrl: '' };
    }
  }

  return null;
}

/**
 * Fetch Vimeo thumbnail via backend proxy (Vimeo oEmbed doesn't support browser CORS).
 * Returns the thumbnail URL string, or '' on failure.
 * @param {string} id
 * @returns {Promise<string>}
 */
async function fetchVimeoThumbnail(id) {
  if (!(id && /^\d+$/.test(id))) {
    return '';
  }
  try {
    const res = await fetch(`/api/vimeo-thumb?id=${id}`);
    if (!res.ok) {
      return '';
    }
    const data = await res.json();
    return data.ok ? data.thumbnailUrl || '' : '';
  } catch {
    return '';
  }
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export { fetchVimeoThumbnail, parseVideoUrl };
